import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Heart } from "lucide-react";
import { useLang } from "@/i18n";
import { IMG } from "@/data/media";
import { L } from "@/components/L";
import { LangSwitcher } from "@/components/LangSwitcher";

const NAV = ["about", "activities", "projects", "team", "gallery", "donate", "contact"];

const MobileMenu = ({ onClose }) => {
  const { t } = useLang();
  return (
    <motion.div
      initial={{ clipPath: "circle(0% at 100% 0%)" }}
      animate={{ clipPath: "circle(150% at 100% 0%)" }}
      exit={{ clipPath: "circle(0% at 100% 0%)" }}
      transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
      className="fixed inset-0 z-[60] bg-[var(--green-night)] text-white flex flex-col"
      data-testid="mobile-menu"
    >
      <div className="wrap flex items-center justify-between h-20">
        <LangSwitcher onPick={onClose} idPrefix="mobile" />
        <button onClick={onClose} aria-label={t.nav.close} data-testid="mobile-menu-close" className="h-11 w-11 grid place-items-center rounded-full bg-white/10"><X size={22} /></button>
      </div>
      <nav className="wrap flex-1 flex flex-col justify-center gap-1" aria-label="Mobile">
        {["home", ...NAV].map((k, i) => (
          <motion.div key={k} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 + i * 0.05, duration: 0.6 }}>
            <L to={k} onClick={onClose} data-testid={`mobile-nav-${k}`} className="font-display text-4xl sm:text-5xl leading-tight hover:text-[var(--yellow)] transition-colors">{t.nav[k]}</L>
          </motion.div>
        ))}
      </nav>
      <div className="wrap pb-10">
        <L to="donate" onClick={onClose} className="btn btn-yellow w-full" data-testid="mobile-donate-btn"><Heart size={16} /> {t.nav.donateNow}</L>
      </div>
    </motion.div>
  );
};

export default function Header() {
  const { t, pageKey } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) {
      if (open) window.__lenis.stop();
      else window.__lenis.start();
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${scrolled ? "bg-[rgba(251,248,241,0.82)] backdrop-blur-xl shadow-[0_1px_0_var(--line)]" : "bg-transparent"}`} data-testid="site-header">
        <div className="wrap flex items-center justify-between gap-4 h-20">
          <L to="home" className="flex items-center gap-3 shrink-0" aria-label="MADE — Home" data-testid="header-logo-link">
            <img src={IMG.logo} alt="Logo MADE" className="h-12 w-auto" width="46" height="60" />
            <span className="hidden sm:flex flex-col leading-none">
              <span className="font-display text-lg tracking-wide">Fundación MADE</span>
              <span className="text-[11px] uppercase tracking-[0.2em] text-[var(--ink-soft)] mt-1">MADE ONLUS</span>
            </span>
          </L>
          <nav className="hidden xl:flex items-center gap-7 text-[15px] font-medium" aria-label="Main">
            {NAV.map((k) => (
              <L key={k} to={k} aria-current={pageKey === k || (k === "projects" && ["laRomana", "villa"].includes(pageKey)) ? "page" : undefined} className="link-u py-1" data-testid={`nav-${k}`}>{t.nav[k]}</L>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:block"><LangSwitcher /></div>
            <div className="sm:hidden"><LangSwitcher compact idPrefix="header-compact" /></div>
            <div className="hidden md:block"><L to="donate" className="btn btn-yellow !py-3" data-testid="header-donate-btn"><Heart size={16} /> {t.nav.donateNow}</L></div>
            <button onClick={() => setOpen(true)} aria-label={t.nav.menu} data-testid="mobile-menu-open" className="xl:hidden h-11 w-11 grid place-items-center rounded-full bg-[var(--green-deep)] text-white"><Menu size={20} /></button>
          </div>
        </div>
      </header>
      <AnimatePresence>{open && <MobileMenu onClose={() => setOpen(false)} />}</AnimatePresence>
    </>
  );
}
